'use client';

import { cloneElement, isValidElement, type CSSProperties, type ReactNode } from 'react';
import { Card } from '@akademi/ui';
import { tx } from './coerce';
import { cardReset, kickerStyle, muted, sectionInner, sectionPad } from './styles';
import { sharedStyles } from '@akademi/site-builder-blocks';
import {
  useBlockRootAttrs,
  useBlockPart,
  useBlockControlStamp,
  BlockRootStamped,
} from '@akademi/site-builder-runtime';
import { resolvePaddingStyle, type PaddingBox } from './resolvePadding';
import { HeadingLine } from './HeadingLine';

/**
 * Generic section wrapper — gives every block consistent vertical
 * rhythm + container width. {@code title} accepts either a string or a
 * ReactNode. ReactNode passes through unchanged; strings (or stray
 * localized bundles) go through {@link tx} so legacy data doesn't show
 * a {@code [object Object]} title.
 *
 * <p>Direct port of ThemeMaker's
 * {@code lms-portal/src/components/blocks/_shared/SectionShell.tsx} —
 * preserves the JSX-vs-string title handling that fixed the original
 * "title disappears" bug across data-fetching blocks.
 */
export function SectionShell({
  title,
  subtitle,
  kicker,
  action,
  headingLine,
  children,
  background,
  style,
  paddingOuter,
  paddingInner,
  titleFontSize,
  boxed,
  maxWidth = 'theme',
  marginBottom,
}: {
  title?: ReactNode;
  subtitle?: string;
  kicker?: string;
  action?: ReactNode;
  /**
   * Draw a decorative gradient rule between the heading and the action
   * (the BTK "Featured" row treatment). Off by default so existing
   * sections are unchanged; admin-toggleable per block instance, which is
   * how it stays limited to the pages that want it.
   */
  headingLine?: boolean;
  children: ReactNode;
  background?: string;
  style?: CSSProperties;
  /** Space-token keys for the outer <section> padding (overrides sectionPad sides). */
  paddingOuter?: PaddingBox;
  /** Space-token keys for the inner content-area padding. */
  paddingInner?: PaddingBox;
  /** Design token size key e.g. "xl", "2xl". Maps to var(--font-size-{key}). */
  titleFontSize?: string;
  /**
   * Opt-in visible card (background + border) around the inner
   * content area, so `paddingOuter` (space outside the box) and
   * `paddingInner` (space inside the box, border-to-content) read as
   * visually distinct. Off by default — most SectionShell consumers
   * are full-bleed page sections that must stay borderless.
   */
  boxed?: boolean;
  /** 'theme' (default) constrains the inner content area to the page's
   *  max-width container token, matching every existing SectionShell
   *  consumer's current behaviour. 'full' lets it span the section
   *  edge-to-edge — same toggle/naming as HeroCarousel's `maxWidth`. */
  maxWidth?: 'full' | 'theme';
  /** Overrides the default `var(--space-6, 24px)` gap below the title/action row. */
  marginBottom?: string;
}) {
  // Stable DOM identity for the block whose root this <section> is —
  // data-<ns>-block / -anchor / -node-id from BlockIdentityContext (set
  // by RenderOneBlock). Empty {} outside the renderer, so this is a
  // no-op for Storybook / standalone use. See blockIdentity.tsx.
  const rootAttrs = useBlockRootAttrs();
  // data-<ns>-part hooks on the shared header elements — one edit here
  // gives every SectionShell-based block the same title / subtitle /
  // header targets for test automation + onboarding. See blockIdentity.
  const part = useBlockPart();
  // Auto-stamp data-<ns>-el on every button / link / field inside the
  // block that `part()` didn't already name — closes the "the author
  // never tagged this control" gap. See useBlockControlStamp.
  const rootRef = useBlockControlStamp<HTMLElement>();

  // Don't coerce JSX titles — tx() would stringify a React element to
  // '' and the title would vanish. Coerce only plain strings + dict
  // bundles.
  const t =
    isValidElement(title) || (title != null && typeof title !== 'string') ? title : tx(title);
  const s = tx(subtitle);
  const k = tx(kicker);

  const hasTitle = isValidElement(t) || (typeof t === 'string' && t.length > 0);

  // Stamp data-<ns>-part="action" onto the header action (a "see all"
  // link, arrows, …) WITHOUT an extra wrapper — clone it and merge the
  // attr in. Non-element actions (a fragment/array) pass through as-is.
  const taggedAction = isValidElement(action)
    ? cloneElement(action, part('action') as Record<string, unknown>)
    : action;

  const outerPad = resolvePaddingStyle(paddingOuter);
  const innerPad = resolvePaddingStyle(paddingInner);
  // `boxed` renders the inner content area through `@akademi/ui`'s <Card>,
  // which brings the border + elevation; only the fill / radius live here.
  // `cardReset` cancels the layout opinions <Card> would otherwise impose
  // (flex column / gap / vertical padding / clipped overflow).
  const boxedStyle: CSSProperties = boxed
    ? {
        ...cardReset,
        background: 'var(--color-surface-card, #ffffff)',
        borderRadius: 'var(--radius-lg, 12px)',
      }
    : {};

  const containerWidthStyle: CSSProperties = maxWidth === 'full' ? {} : sectionInner;

  const InnerBox = boxed ? Card : 'div';

  return (
    <section
      {...rootAttrs}
      ref={rootRef}
      style={{ ...sectionPad, ...outerPad, background: background ?? 'transparent', ...style }}
    >
      <InnerBox
        className={maxWidth === 'full' ? undefined : 'sb-app-max-width'}
        style={{ ...containerWidthStyle, ...boxedStyle, ...innerPad }}
      >
        {k && (
          <div {...part('kicker')} style={{ ...kickerStyle, marginBottom: 8 }}>
            {k}
          </div>
        )}
        {(hasTitle || action) && (
          <div
            {...part('header')}
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: marginBottom ?? 'var(--space-6, 24px)',
              gap: 16,
            }}
          >
            {hasTitle && (
              <div>
                <h2
                  {...part('title')}
                  style={
                    titleFontSize
                      ? {
                          ...sharedStyles.sectionTitle,
                          fontSize: `var(--font-size-${titleFontSize}, ${titleFontSize})`,
                        }
                      : sharedStyles.sectionTitle
                  }
                >
                  {t}
                </h2>
                {s && (
                  <div {...part('subtitle')} style={{ ...muted, marginTop: 4 }}>
                    {s}
                  </div>
                )}
              </div>
            )}
            {/* Only ever drawn next to a real heading — a rule on its own
                (e.g. a block that renders its own header and passes no
                title) would read as a stray divider. */}
            {hasTitle && headingLine && <HeadingLine />}
            {taggedAction}
          </div>
        )}
        <BlockRootStamped>{children}</BlockRootStamped>
      </InnerBox>
    </section>
  );
}
